import { css } from 'emotion';
import camelCase from 'lodash/camelCase';
import * as React from 'react';

import { BREAKPOINT_SMALL } from '../consts/breakpoints';
import { Gender } from '../enums/Gender';
import { Feature, OptionValue, Picker } from '../state/models';
import { GenerateGroupOptionItem } from '../utils/groupOptionSelector/GenerateGroupOptionItem';
import { ColorItem } from './ColorItem';
import { GroupOptionItem } from './GroupOptionItem';
import { GroupOptionSelector } from './GroupOptionSelector';

const selector = css`
  max-width: 350px;
  margin: 20px auto 30px auto;

  @media (max-width: ${BREAKPOINT_SMALL}) {
    max-width: 100%;
  }
`;

const firstPicker = css`
  border: 1px solid #e4e4e4;
  border-bottom: 0;
  border-radius: 10px 10px 0 0;
  background-color: #f6f2f3;
`;

const lowerPicker = css`
  border: 1px solid #e4e4e4;
`;

interface IFeature {
  feature: Feature;
  isSelected: boolean;
}

interface IProps {
  bookId?: number;
  avatar?: any;
  features: Array<IFeature>;
  pickers: Array<Picker> | null;
  params: Record<string, string>;
  avatarGender: Gender;
  onOptionSelect(value: string): void;
  onPickerSelect(uid: string, value: OptionValue, values: Array<OptionValue>): any;
}

export const FormSelectors = ({
  bookId,
  avatar,
  onOptionSelect,
  onPickerSelect,
  pickers,
  params,
  features,
  avatarGender,
}: IProps) => {
  return (
    <div className={selector}>
      <div className={firstPicker}>
        <GroupOptionSelector>
          {features &&
            features.map(({ isSelected, feature }) => {
              return GenerateGroupOptionItem(feature.icon, isSelected, feature.icon, onOptionSelect, bookId, avatar);
            })}
        </GroupOptionSelector>
      </div>
      <div className={lowerPicker}>
        {pickers &&
          pickers.map((picker) => {
            if (picker.type === 'color') {
              return (
                <GroupOptionSelector key={picker.uid}>
                  {picker.optionValues
                    .filter(({ genderId }) => (genderId as Gender) === avatarGender || (genderId as Gender) === 1)
                    .map((value) => (
                      <ColorItem
                        key={value.uid}
                        color={value.pickerValue}
                        isSelected={params[camelCase(picker.uid)] === value.uid}
                        onClick={onPickerSelect(picker.uid, value, picker.optionValues)}
                      />
                    ))}
                </GroupOptionSelector>
              );
            }

            if (picker.uid === 'sex') {
              return (
                <GroupOptionSelector key={picker.uid}>
                  {picker.optionValues.map((value) => {
                    const gender = value.uid === 'f' ? 'female' : 'male';

                    return GenerateGroupOptionItem(
                      gender,
                      params[camelCase(picker.uid)] === value.uid,
                      value.uid,
                      onPickerSelect(picker.uid, value, picker.optionValues),
                      bookId,
                      avatar,
                    );
                  })}
                </GroupOptionSelector>
              );
            }

            if (picker.uid === 'glasses') {
              return (
                <GroupOptionSelector key={picker.uid}>
                  {picker.optionValues
                    .filter(({ genderId }) => (genderId as Gender) === avatarGender || (genderId as Gender) === 1)
                    .map((value: OptionValue) => {
                      const hasGlasses = value.pickerValue ? 'hasGlasses' : 'noGlasses';

                      return GenerateGroupOptionItem(
                        hasGlasses,
                        value.pickerValue === params.glasses,
                        value.uid,
                        onPickerSelect(picker.uid, value, picker.optionValues),
                      );
                    })}
                </GroupOptionSelector>
              );
            }

            if (picker.type === 'picker') {
              return (
                <GroupOptionSelector key={picker.uid}>
                  {picker.optionValues
                    .filter(({ genderId }) => (genderId as Gender) === avatarGender || (genderId as Gender) === 1)
                    .map((value: any) => {
                      return (
                        <GroupOptionItem
                          key={value.uid}
                          item={value.pickerValue.thumbnail.medium.url}
                          onClick={onPickerSelect(picker.uid, value, picker.optionValues)}
                          isSelected={params[camelCase(picker.uid)] === value.uid}
                        />
                      );
                    })}
                </GroupOptionSelector>
              );
            }
          })}
      </div>
    </div>
  );
};
